import { useState } from "react";
import { Edit2, X, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Section {
  id: string;
  title: string;
  content: string;
}

interface Service {
  id: string;
  title: string;
  description: string;
  expanded: boolean;
}

export default function KeplerClone() {
  const [editMode, setEditMode] = useState(false);
  const [sections, setSections] = useState<Section[]>([
    {
      id: "hero-title",
      title: "Hero Title",
      content: "TRANSFORM YOUR BRAND STORY",
    },
    {
      id: "hero-desc",
      title: "Hero Description",
      content:
        "KEPLER is an emerging Egyptian automotive company dedicated to delivering high-quality integrated car services. We import cutting-edge technology and genuine spare parts from Germany to offer our customers a safe, luxurious, and reliable driving experience.",
    },
    {
      id: "services-heading",
      title: "Services Heading",
      content: "OUR SERVICES",
    },
    {
      id: "services-subheading",
      title: "Services Subheading",
      content:
        "Explore the world through the lens of our visual capabilities, and find what you love.",
    },
  ]);

  const [services, setServices] = useState<Service[]>([
    {
      id: "service-1",
      title: "Digital Marketing",
      description:
        "Data-driven campaigns that amplify your reach and maximize ROI across all digital channels.",
      expanded: false,
    },
    {
      id: "service-2",
      title: "Brand Strategy",
      description: "Strategic brand positioning that sets you apart.",
      expanded: false,
    },
    {
      id: "service-3",
      title: "Social Media Management",
      description: "Engaging content that builds community and loyalty.",
      expanded: false,
    },
    {
      id: "service-4",
      title: "SEO/SEM",
      description: "Visibility and conversion optimization.",
      expanded: false,
    },
    {
      id: "service-5",
      title: "Content Creation",
      description: "Compelling stories that resonate.",
      expanded: false,
    },
    {
      id: "service-6",
      title: "Web Design",
      description: "Beautiful, functional digital experiences.",
      expanded: false,
    },
  ]);

  const updateSection = (id: string, content: string) => {
    setSections(
      sections.map((s) => (s.id === id ? { ...s, content } : s))
    );
  };

  const updateService = (
    id: string,
    field: "title" | "description",
    value: string
  ) => {
    setServices(
      services.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      )
    );
  };

  const toggleService = (id: string) => {
    setServices(
      services.map((s) =>
        s.id === id ? { ...s, expanded: !s.expanded } : s
      )
    );
  };

  const addService = () => {
    setServices([
      ...services,
      {
        id: `service-${Date.now()}`,
        title: "New Service",
        description: "Add your service description here.",
        expanded: false,
      },
    ]);
  };

  const deleteService = (id: string) => {
    setServices(services.filter((s) => s.id !== id));
  };

  const getSection = (id: string) =>
    sections.find((s) => s.id === id)?.content || "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Header */}
      <header className="border-b border-slate-800 px-6 py-4 flex justify-between items-center sticky top-0 z-50 bg-slate-950/95 backdrop-blur">
        <h1 className="text-2xl font-bold tracking-tight">KEPLER©</h1>
        <Button
          onClick={() => setEditMode(!editMode)}
          variant={editMode ? "default" : "outline"}
          size="sm"
        >
          {editMode ? (
            <>
              <Check className="w-4 h-4 mr-2" /> Done Editing
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4 mr-2" /> Edit Content
            </>
          )}
        </Button>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-slate-900 to-slate-950">
        <div className="max-w-3xl">
          <div className="mb-8 inline-block px-4 py-2 rounded-full border border-violet-500/30 bg-violet-500/5">
            <span className="text-sm text-violet-400">
              AUTHENTICATED DISTRIBUTER ✓
            </span>
          </div>

          {editMode ? (
            <div className="space-y-4">
              <textarea
                value={getSection("hero-title")}
                onChange={(e) =>
                  updateSection("hero-title", e.target.value)
                }
                className="w-full text-4xl font-bold text-slate-50 bg-slate-800 p-3 rounded border border-slate-700 resize-none"
              />
              <textarea
                value={getSection("hero-desc")}
                onChange={(e) =>
                  updateSection("hero-desc", e.target.value)
                }
                className="w-full text-lg text-slate-300 bg-slate-800 p-3 rounded border border-slate-700 resize-none"
              />
            </div>
          ) : (
            <>
              <h2 className="text-5xl font-bold mb-6 leading-tight">
                {getSection("hero-title")}
                <span className="text-violet-400">
                  {" "}
                  BRAND STORY
                </span>
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed max-w-2xl">
                {getSection("hero-desc")}
              </p>
            </>
          )}

          <div className="flex gap-4 mt-10">
            <button className="px-8 py-3 bg-violet-500 hover:bg-violet-600 rounded-full font-semibold text-slate-50 transition">
              SCHNELL ▶ →
            </button>
            <button className="px-8 py-3 border border-slate-600 hover:border-violet-500 rounded-full font-semibold transition">
              Contact With Us
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="px-6 py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto">
          {editMode ? (
            <div className="space-y-4 mb-8">
              <textarea
                value={getSection("services-heading")}
                onChange={(e) =>
                  updateSection("services-heading", e.target.value)
                }
                className="w-full text-3xl font-bold text-slate-50 bg-slate-800 p-3 rounded border border-slate-700"
              />
              <textarea
                value={getSection("services-subheading")}
                onChange={(e) =>
                  updateSection("services-subheading", e.target.value)
                }
                className="w-full text-lg text-slate-300 bg-slate-800 p-3 rounded border border-slate-700"
              />
            </div>
          ) : (
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                {getSection("services-heading").split(" ")[0]}{" "}
                <span className="text-violet-400">
                  {getSection("services-heading").split(" ").slice(1).join(" ")}
                </span>
              </h2>
              <p className="text-slate-300 text-lg">
                {getSection("services-subheading")}
              </p>
            </div>
          )}

          {/* Services List */}
          <div className="space-y-4">
            {services.map((service) => (
              <div
                key={service.id}
                className="border-b border-slate-800 pb-4 last:border-0"
              >
                <div className="flex items-center justify-between group">
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => !editMode && toggleService(service.id)}
                  >
                    {editMode ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={service.title}
                          onChange={(e) =>
                            updateService(
                              service.id,
                              "title",
                              e.target.value
                            )
                          }
                          className="w-full text-xl font-semibold text-slate-50 bg-slate-800 p-2 rounded border border-slate-700"
                        />
                        <textarea
                          value={service.description}
                          onChange={(e) =>
                            updateService(
                              service.id,
                              "description",
                              e.target.value
                            )
                          }
                          className="w-full text-slate-300 bg-slate-800 p-2 rounded border border-slate-700 resize-none"
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold text-slate-50 mb-2">
                          {service.title}
                        </h3>
                        {service.expanded && (
                          <p className="text-slate-400">
                            {service.description}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    {editMode ? (
                      <button
                        onClick={() => deleteService(service.id)}
                        className="p-2 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    ) : (
                      <button className="text-violet-400 text-2xl hover:text-violet-300">
                        {service.expanded ? "−" : "+"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {editMode && (
            <button
              onClick={addService}
              className="mt-6 flex items-center gap-2 px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 rounded border border-violet-500/30"
            >
              <Plus className="w-4 h-4" /> Add Service
            </button>
          )}
        </div>
      </section>

      {/* Excellence Section */}
      <section className="px-6 py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden mb-10 h-64 flex items-center justify-center border border-slate-700">
            <span className="text-slate-600">Image Placeholder</span>
          </div>
          <h2 className="text-4xl font-bold mb-2">
            WE'RE PASSIONATE ABOUT DIGITAL
            <span className="text-violet-400"> EXCELLENCE</span>
          </h2>
          <div className="mt-8 space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-slate-50">
                Quality at the forefront
              </h3>
              <p className="text-slate-400">
                Every project is crafted with attention to detail and a
                commitment to deliver exceptional results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-slate-50">
                Focus on measurable impact
              </h3>
              <p className="text-slate-400">
                Our focus is on delivering tangible, measurable results that
                drive your business forward.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
